import { toast } from 'react-toastify';
import { compose, over, lensProp } from 'ramda';

import {
  createNewEstablishment,
  updateEstablishment,
  fetchBusinessInfo,
  activePremiumPackage,
  disablePremiumPackage,
  debitWarning,
  appPermission,
  fetchSuperlogicaList,
  fetchBusinessConceptList,
  fetchJumpCarStoreDownload
} from './service';

import {
  setEstablishmentId,
  setEstablishmentUuid,
  setBusinessInfo,
  handleLoading,
  handlePostalCode,
  setEditingState,
  setEstablishmentTypes,
  setEstablishmentErrors,
  clearEstablishmentErrors,
  setSuperlogicaList,
  setIsJumpCarStoreDownloadLoading,
  setIsJumpCarStoreDownloadAvailable,
  setJumpCarStoreDownload
} from './actions';

import { setNewEstablishment, setUpdatedEstablishment } from '../../Login/store/actions';
import { fetchAddress } from '../../../helpers/externalServices';
import { formatToOnlyNumbers, formatToOnlyNumbersAndLetters } from '../../../helpers/formatters';

export const loadBusinessInfo = params => dispatch => {
  dispatch(handleLoading(true));

  return fetchBusinessInfo(params)
    .then(({ data }) => dispatch(setBusinessInfo(data)))
    .finally(() => dispatch(handleLoading(false)));
}

export const upsertEstablishment = params => dispatch => {
  const formatParams = compose(
    over(lensProp('phone'), formatToOnlyNumbers),
    over(lensProp('postalCode'), formatToOnlyNumbers),
    over(lensProp('documentId'), formatToOnlyNumbersAndLetters)
  );

  dispatch(handleLoading(true));

  if(!!params.establishmentId) {
    delete params.resaleId;
  }

  delete params['appPermission'];

  if(!params.establishmentId) {
    delete params['establishmentId'];

    return createNewEstablishment(formatParams(params))
      .then(({ data }) => {
        const { establishmentId, uuid, establishmentTypes } = data;

        if(!params?.isInitialRegistration) {
          toast.success('Cadastro efetuado com sucesso! Procure por "Jump Park" na Play Store e comece a operar.');
        }

        if(params?.isInitialRegistration) {
          if(params?.establishmentTypes?.includes(1)) {
            toast.success('Estabelecimento criado. Agora cadastre sua tabela de preço');
          }
        }

        dispatch(setNewEstablishment(data));
        dispatch(setEstablishmentId({ establishmentId }));
        dispatch(setEstablishmentUuid({ establishmentUuid: uuid }));
        dispatch(setEstablishmentTypes(establishmentTypes));

        return Promise.resolve(data);
      })
      .catch(error => {
        if(!error) {
          dispatch(clearEstablishmentErrors());
        }

        dispatch(setEstablishmentErrors(error));

        return Promise.reject();
      })
      .finally(() => dispatch(handleLoading(false)));
  }

  return updateEstablishment(formatParams(params))
    .then(({ data }) => {
      toast.success('Edição feita! Para atualizar no aplicativo saia e acesse novamente no próprio aplicativo.');
      dispatch(setUpdatedEstablishment(data));
      return Promise.resolve({ ...data, isUpdated: true });
    })
    .catch(error => {
      if(!error) {
        dispatch(clearEstablishmentErrors());
      }

      dispatch(setEstablishmentErrors(error));

      return Promise.reject();
    })
    .finally(() => dispatch(handleLoading(false)));
}

export const changeEstablishmentPermission = (userId, establishmentId, params) => dispatch => {
  dispatch(handleLoading(true));

  return appPermission(userId, establishmentId, params)
    .then(() => {
      toast.success('Permissão do estabelecimento atualizada!');
      dispatch(setEditingState(false));
      dispatch(loadBusinessInfo({ userId, establishmentId }));
    })
    .catch(() => toast.error('Erro ao atualizar a permissão do estabelecimento.'))
    .finally(() => dispatch(handleLoading(false)));
}

export const loadAddress = value => dispatch => {
  const postalCode = value.replace(/-/g, '');

  dispatch(handlePostalCode(true));

  return fetchAddress(postalCode)
    .then(data => {
      const { erro } = data;

      if(erro) {
        dispatch(handlePostalCode(false));
        return toast.error('Não conseguimos encontrar o seu CEP');
      }

      return data;
    })
    .catch(() => toast.error('Problemas no servidor, tente novamente mais tarde'))
    .finally(() => dispatch(handlePostalCode(false)));
}

export const setActivePremiumPackage = (userId, establishmentId) => dispatch => {
  return activePremiumPackage(userId, establishmentId)
    .then(({ data }) => {
      toast.success(data);
      dispatch(loadBusinessInfo({ userId, establishmentId }));
    })
    .catch(() => toast.error('Houve um erro ao ativar o Pacote Premium.'));
}

export const setDisablePremiumPackage = (userId, establishmentId) => dispatch => {
  return disablePremiumPackage(userId, establishmentId)
    .then(({ data }) => {
      toast.success(data);
      dispatch(loadBusinessInfo({ userId, establishmentId }));
    })
    .catch(() => toast.error('Houve um erro ao desativar o Pacote Premium.'));
}

export const setDebitWarning = (userId, establishmentId, params) => dispatch => {
  dispatch(handleLoading(true));

  return debitWarning(userId, establishmentId, params)
    .then(({ data }) => {
      toast.success(data);
      dispatch(loadBusinessInfo({ userId, establishmentId }));
    })
    .finally(() => dispatch(handleLoading(false)));
}

export const loadSuperlogicaList = userId => dispatch => {
  return fetchSuperlogicaList(userId)
    .then(({ data }) => dispatch(setSuperlogicaList(data)))
    .catch(() => dispatch(setSuperlogicaList([])));
}

export const loadBusinessConceptList = (userId, businessUuid) => dispatch => {
  return fetchBusinessConceptList(userId, businessUuid)
    .then(({ data }) => dispatch(setSuperlogicaList(data)))
    .catch(() => dispatch(setSuperlogicaList([])));
}

export const loadJumpCarStoreDownload = userId => dispatch => {
  dispatch(setIsJumpCarStoreDownloadLoading(true));

  return fetchJumpCarStoreDownload(userId)
    .then(({ data }) => {
      dispatch(setIsJumpCarStoreDownloadAvailable(true));
      dispatch(setJumpCarStoreDownload(data));
    })
    .catch(() => dispatch(setIsJumpCarStoreDownloadAvailable(false)))
    .finally(() => dispatch(setIsJumpCarStoreDownloadLoading(false)));
}